import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, BookOpen, Settings as SettingsIcon, UserPlus, Sparkles, Scissors, Heart,
  FileText, Send, Camera, Calendar, Users, ListChecks, MapPin, Lightbulb, Shield, LifeBuoy, Mail, X,
} from "lucide-react";
import Footer from "@/components/Footer";
import BrandLogo from "@/components/BrandLogo";
import useSupportEmail from "@/lib/useSupportEmail";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

// Sticky TOC + long single-page guide. Anchor IDs match top-level sections.
const TOC = [
  { id: "intro", label: "1. Introduzione", icon: BookOpen },
  { id: "configurazione", label: "2. Prima configurazione", icon: SettingsIcon },
  { id: "flusso-cliente", label: "3. Flusso completo cliente", icon: UserPlus },
  { id: "dashboard-cliente", label: "4. Dashboard cliente", icon: Heart },
  { id: "wedding", label: "5. Sezione Wedding", icon: Heart },
  { id: "makeup-hair", label: "6. Makeup & Hair", icon: Sparkles },
  { id: "preventivo", label: "7. Preventivo", icon: FileText },
  { id: "invio", label: "8. Invio preventivo + contratto", icon: Send },
  { id: "foto-pdf", label: "9. Foto & PDF", icon: Camera },
  { id: "calendario-team", label: "10. Calendario & Team", icon: Calendar },
  { id: "timeline", label: "11. Timeline evento", icon: ListChecks },
  { id: "mappa", label: "12. Mappa", icon: MapPin },
  { id: "best-practice", label: "13. Consigli operativi", icon: Lightbulb },
];

export default function Guide() {
  const [activeId, setActiveId] = useState(TOC[0].id);
  const supportEmail = useSupportEmail();
  const navigate = useNavigate();
  const { user } = useAuth();

  // "Chiudi" / brand-logo behaviour: NEVER touch the auth session.
  //   - Auth users → back to the in-app dashboard (the natural anchor
  //     they came from via the sidebar "Guida" link). We try
  //     `navigate(-1)` first so it feels like closing a modal when the
  //     user arrived via a link click; if there's no history (direct
  //     URL hit / refresh) we fall back to /app/dashboard.
  //   - Anonymous users → marketing landing.
  // Why this exists: previously the header had a `<Link to="/">`. Auth
  // users landed on the public landing page and *thought* they'd been
  // logged out (the sidebar disappears). They actually weren't — but
  // the perceived bug was the same.
  const closeGuide = () => {
    if (user) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/app/dashboard");
      }
    } else {
      navigate("/");
    }
  };
  const brandHomeTarget = user ? "/app/dashboard" : "/";

  // Scroll-spy: highlight TOC entry when its section is in view.
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    TOC.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-secondary/30 flex flex-col" data-testid="guide-page">
      <header className="bg-white border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to={brandHomeTarget}
            className="flex items-center gap-2 font-serif-heading text-neutral-800 hover:text-primary transition-colors"
            data-testid="guide-home-link"
          >
            <BrandLogo size={22} />
            AI Bridal Manager
          </Link>
          <button
            type="button"
            onClick={closeGuide}
            className="text-xs text-neutral-500 hover:text-primary inline-flex items-center gap-1 px-2 py-1 rounded-sm"
            data-testid="guide-close-button"
          >
            <X className="w-3.5 h-3.5" strokeWidth={1.5} /> Chiudi guida
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-12 sm:py-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="label-uppercase text-primary">Manuale operativo</span>
          <h1 className="font-serif-heading text-4xl sm:text-5xl tracking-tight mt-3">
            Guida all'utilizzo
          </h1>
          <p className="text-sm sm:text-base text-neutral-600 mt-3">
            Tutto quello che ti serve per usare AI Bridal Manager dall'inizio alla fine,
            senza bisogno di assistenza. Configurazione, flusso cliente, team, calendario, best practice.
          </p>
        </div>

        {/* Prominent support card — posizionata SUBITO sotto il titolo
            così è immediatamente visibile non appena si apre la guida.
            Sfondo champagne tenue + bordo accent + CTA primaria. */}
        <div
          className="mt-10 max-w-3xl mx-auto bg-gradient-to-br from-[#FBF6EC] to-[#F5EBD7] border-2 border-primary/40 rounded-sm p-6 sm:p-8 shadow-sm"
          data-testid="guide-support-box"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <span className="w-14 h-14 rounded-full bg-primary/15 ring-1 ring-primary/30 flex items-center justify-center shrink-0">
              <LifeBuoy className="w-7 h-7 text-primary" strokeWidth={1.5} />
            </span>
            <div className="flex-1 min-w-0">
              <h2 className="font-serif-heading text-2xl sm:text-3xl text-neutral-900">
                Hai bisogno di assistenza?
              </h2>
              <p className="text-sm sm:text-base text-neutral-700 mt-2 leading-relaxed">
                Per supporto tecnico o domande sull'utilizzo della piattaforma,
                contattaci all'indirizzo{" "}
                <a
                  href={`mailto:${supportEmail}`}
                  className="text-primary font-medium underline-offset-2 hover:underline"
                  data-testid="guide-support-email"
                >
                  {supportEmail}
                </a>
                .
              </p>
            </div>
            <Button
              asChild
              size="lg"
              className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 w-full sm:w-auto"
              data-testid="guide-support-cta"
            >
              <a href={`mailto:${supportEmail}`}>
                <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Contatta il supporto
              </a>
            </Button>
          </div>
        </div>


        <div className="mt-12 lg:grid lg:grid-cols-[240px_1fr] lg:gap-12">
          {/* Sticky TOC */}
          <aside className="hidden lg:block">
            <nav className="sticky top-8 border-l border-border pl-5" aria-label="Indice guida">
              <span className="label-uppercase text-neutral-500 mb-3 block">Indice</span>
              <ul className="space-y-1.5 text-sm">
                {TOC.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className={`block py-1 pl-2 -ml-px border-l-2 transition-colors ${
                        activeId === s.id
                          ? "border-primary text-primary font-medium"
                          : "border-transparent text-neutral-600 hover:text-neutral-900"
                      }`}
                      data-testid={`toc-${s.id}`}
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Content */}
          <article className="prose-bridal max-w-none space-y-10">
            <Section id="intro" icon={BookOpen} title="1. Introduzione">
              <p>
                <strong>AI Bridal Manager</strong> è un software pensato per chi lavora nel mondo wedding —
                makeup artist, hairstylist, studi di acconciatura e team beauty — che vuole gestire ogni sposa in modo
                professionale, dalla prima richiesta fino al giorno del matrimonio.
              </p>
              <p>Con AI Bridal Manager puoi:</p>
              <BulletList items={[
                "Raccogliere richieste dalle clienti tramite un link pubblico (lead).",
                "Centralizzare ogni cliente con scheda dedicata, storico, prove e contratti.",
                "Generare preventivi e contratti professionali multilingua.",
                "Inviare preventivo e contratto firmabili online dal cliente.",
                "Coordinare il team con assegnazione operatori e calendario condiviso.",
                "Costruire la timeline operativa del giorno e condividerla con la cliente.",
              ]} />
              <Callout tone="primary">
                Il software è già white-label e GDPR-ready: puoi iniziare a lavorare subito dopo la prima
                configurazione delle impostazioni.
              </Callout>
            </Section>

            <Section id="configurazione" icon={SettingsIcon} title="2. Prima configurazione (fondamentale)">
              <Callout tone="warn">
                <strong>Prima di lavorare con le clienti, completa la configurazione.</strong> Investire 10 minuti
                qui ti farà risparmiare ore dopo: contratti, preventivi e link pubblici nasceranno già corretti.
              </Callout>

              <h3>2.1 Accedi alle Impostazioni</h3>
              <p>
                Dal menu laterale clicca <Pill>Impostazioni</Pill>. Trovi tutte le voci di configurazione raggruppate
                in una pagina sola.
              </p>

              <h3>2.2 Dati attività e fatturazione</h3>
              <BulletList items={[
                "Compila i dati anagrafici dello studio (denominazione, indirizzo).",
                "Imposta i dati di fatturazione (privato o azienda con P.IVA): verranno usati nei PDF di preventivo e contratto.",
                "Se sei in regime forfettario, lo selezionerai poi sul singolo preventivo (non qui).",
              ]} />

              <h3>2.3 Contratto: Standard vs Personalizzato</h3>
              <p>
                AI Bridal Manager fornisce un <strong>contratto standard</strong> già pronto, conforme e bilingue.
                Se vuoi usare un tuo modello personalizzato:
              </p>
              <BulletList items={[
                "Vai in Impostazioni → Contratto personalizzato.",
                'Clicca "Carica template standard" se vuoi partire dal nostro modello e modificarlo.',
                "Personalizza condizioni generali e termini di pagamento.",
                "Salva.",
              ]} />
              <p>
                Sulla scheda di ogni cliente potrai poi scegliere se applicare il contratto Standard o il
                tuo Personalizzato — il cambio è istantaneo e visibile sul PDF.
              </p>

              <h3>2.4 Sicurezza — Cambio password</h3>
              <p>
                In Impostazioni → Sicurezza puoi cambiare la password in autonomia. Inserisci la password
                attuale, la nuova (minimo 8 caratteri) e la conferma. Il toggle 👁 ti permette di vedere
                i caratteri durante la digitazione.
              </p>

              <h3>2.5 Team operatori (consigliato)</h3>
              <p>
                Se lavori con un team, vai in <Pill>Team</Pill> dal menu laterale e aggiungi i tuoi
                collaboratori. Per ognuno scegli ruolo (Makeup, Hair, Entrambi, Assistente) e un colore:
                quel colore comparirà su calendario ed eventi per identificare visivamente l'operatore.
              </p>

              <h3>2.6 Link profilazione (Lead Machine)</h3>
              <p>
                In Impostazioni trovi il tuo link pubblico per raccogliere richieste sposa. Copialo e condividilo
                su Instagram, sito, biglietti da visita: ogni richiesta entra automaticamente nei tuoi clienti
                con stato <em>Lead</em>.
              </p>

              <Callout tone="primary">
                <strong>Regola d'oro:</strong> prima configuro (dati attività, contratto, team, link lead),
                poi lavoro. La configurazione è anche una scelta di posizionamento.
              </Callout>
            </Section>

            <Section id="flusso-cliente" icon={UserPlus} title="3. Flusso completo cliente">
              <h3>3.1 Primo contatto — Link di profilazione</h3>
              <p>
                Il <strong>link pubblico</strong> (Lead Machine) ti permette di non rincorrere mai più WhatsApp
                pieni di domande. Funziona così:
              </p>
              <BulletList items={[
                "Tu copi il link dalla dashboard o dalle Impostazioni.",
                "Lo invii alla cliente o lo metti in bio Instagram.",
                "La cliente compila un form strutturato: nome, partner, data matrimonio, location, servizi richiesti, note.",
                "All'invio del form la cliente entra automaticamente nel tuo CRM con stato Lead e badge \"Nuovo\".",
                "Tu ricevi una notifica visiva (banner Dashboard + numero accanto a Clienti nel menu).",
              ]} />
              <p>
                Questo ti permette di arrivare al primo contatto già con un quadro completo: data evento,
                location, servizi e budget di massima.
              </p>

              <h3>3.2 Creazione scheda cliente</h3>
              <p>Hai due modalità:</p>
              <BulletList items={[
                "Automatica: il lead entra da solo dal link pubblico (preferito).",
                "Manuale: dalla pagina Clienti → Nuovo cliente → compila i dati base.",
              ]} />
              <p>
                Una volta aperta la scheda, tutto è organizzato in tab navigabili: Wedding, Makeup, Hair,
                Preventivo, Contratto, Timeline, Mappa.
              </p>

              <Callout tone="primary">
                Tutte le modifiche su una scheda cliente vengono <strong>salvate automaticamente</strong>.
                Vedrai l'indicatore di salvataggio in alto a destra; non c'è bisogno di un bottone "Salva".
              </Callout>
            </Section>

            <Section id="dashboard-cliente" icon={Heart} title="4. Dashboard cliente">
              <p>
                La scheda cliente è il tuo centro di controllo per quella sposa. In alto trovi nome, partner
                e badge dello stato (Lead / Preventivo inviato / Confermata / Completato). I dati sono
                organizzati in tab:
              </p>
              <BulletList items={[
                "Wedding — dati evento (data, location, orari).",
                "Makeup — scheda trucco, prodotti, prove, operatore principale e assistenti.",
                "Hair — scheda capelli, prove, operatore principale e assistenti.",
                "Preventivo — voci, totale, modalità fiscale, lingua.",
                "Contratto — stato firma, modello applicato, PDF.",
                "Timeline — sequenza operativa del giorno.",
                "Mappa — visualizzazione delle location coinvolte.",
              ]} />
              <p>
                Cambia tab senza paura di perdere dati: ogni modifica viene persistita in tempo reale.
              </p>
            </Section>

            <Section id="wedding" icon={Heart} title="5. Sezione Wedding">
              <p>
                È il cuore informativo dell'evento. Inserisci con precisione:
              </p>
              <BulletList items={[
                "Data matrimonio.",
                "Orario cerimonia.",
                "Orario uscita sposa dalla location di preparazione (fondamentale per la timeline).",
                "Orario inizio lavoro del team.",
                "Numero di persone da preparare.",
                "Location preparazione (casa sposa / hotel) e location cerimonia.",
                "Note evento (allergie, dettagli, preferenze).",
              ]} />
              <Callout tone="warn">
                L'<strong>orario uscita sposa</strong> è il dato più sensibile: da lì calcoli tutto a ritroso.
                Inseriscilo subito, anche stimato — lo aggiusterai quando avrai conferma.
              </Callout>
            </Section>

            <Section id="makeup-hair" icon={Sparkles} title="6. Makeup & Hair">
              <p>
                Le tab Makeup e Hair sono pensate per essere il tuo "diario di bordo" per ogni servizio.
              </p>

              <h3>6.1 Inserimento prove</h3>
              <BulletList items={[
                "Imposta data, ora e luogo della prova.",
                "Quando l'hai fatta, attiva il toggle Completato.",
                "Aggiungi fino a 3 foto per ogni prova (caricamento da telefono o computer).",
                "Aggiungi allegati PDF: storyboard, mood, schede prodotto.",
              ]} />

              <h3>6.2 Assegnazione operatori</h3>
              <p>Sopra la scheda trovi due card dedicate al team:</p>
              <BulletList items={[
                'Operatore trucco / Operatore capelli — un membro singolo, scelto dal dropdown filtrato per ruolo.',
                'Operatori assistenti — multi-select: aggiungi più collaboratori (Assistenti o "Entrambi").',
              ]} />
              <p>
                I colori dei membri del team appaiono come pallini nei badge: questo rende il calendario
                immediatamente leggibile.
              </p>

              <h3>6.3 Scheda servizio</h3>
              <p>
                Sotto trovi la scheda dettagliata del servizio: prodotti per Base viso, Occhi, Labbra (per
                Makeup) e Acconciatura, Accessori (per Hair). Compila come fosse il tuo block notes — ti
                ritroverai pronto al matrimonio.
              </p>
            </Section>

            <Section id="preventivo" icon={FileText} title="7. Preventivo">
              <h3>7.1 Creare il preventivo</h3>
              <BulletList items={[
                "Nella tab Preventivo aggiungi una o più voci (descrizione + importo).",
                "Imposta la lingua del documento (italiano, inglese, spagnolo, francese): il PDF verrà tradotto automaticamente.",
                "Scegli il regime fiscale: Standard (con IVA 22%) o Forfettario (esente IVA, riferimento L.190/2014).",
                "Verifica il totale calcolato in tempo reale.",
              ]} />

              <h3>7.2 Modifiche e revisioni</h3>
              <p>
                Tutte le modifiche al preventivo sono salvate in autosave. Se la cliente chiede di rivedere
                qualcosa, modifichi le voci e rigeneri il PDF in un click.
              </p>

              <h3>7.3 Visualizza il PDF</h3>
              <p>
                Clicca <Pill>Visualizza PDF</Pill> per controllare il preventivo finito prima di inviarlo.
                Stile elegante, intestazione studio, totali, condizioni, lingua scelta.
              </p>
            </Section>

            <Section id="invio" icon={Send} title="8. Invio preventivo + contratto">
              <p>
                In alto sulla scheda cliente trovi il bottone <Pill>Invia preventivo + contratto</Pill>.
                Genera un link privato per la cliente che potrà:
              </p>
              <BulletList items={[
                "Vedere il preventivo nella sua lingua.",
                "Vedere il contratto (Standard o Personalizzato in base alla scelta sulla scheda).",
                "Firmare elettronicamente il contratto.",
              ]} />

              <h3>8.1 Standard vs Personalizzato</h3>
              <p>
                Sul tab Contratto trovi un selettore Tipo contratto:
              </p>
              <BulletList items={[
                "Standard — usa il template ufficiale, sempre conforme. Perfetto per la maggior parte dei casi.",
                "Personalizzato — applica le condizioni e i termini che hai salvato in Impostazioni → Contratto personalizzato. Usalo quando hai clausole specifiche del tuo studio.",
              ]} />
              <p>
                Il cambio è istantaneo, autosave, e si riflette subito nel PDF. Sotto il selettore appare il
                link <em>"Modifica contratto personalizzato"</em> che ti porta direttamente alle Impostazioni.
              </p>

              <Callout tone="primary">
                Quando la cliente firma, lo stato del contratto passa a <strong>Firmato</strong> e l'evento
                appare automaticamente nella sezione Eventi e nel calendario.
              </Callout>
            </Section>

            <Section id="foto-pdf" icon={Camera} title="9. Foto & PDF (condivisione prove)">
              <p>
                Dopo aver fatto la prova trucco o capelli, condividere il risultato con la cliente è un
                touchpoint che la conferma nel tuo livello professionale.
              </p>
              <BulletList items={[
                "Carica foto e PDF nella tab Makeup/Hair (sezione Prova).",
                'Clicca "Condividi prova": il sistema crea un link privato per la cliente.',
                "Scegli come inviarlo: WhatsApp, Email, condivisione nativa, copia link, copia messaggio bilingue (IT+EN).",
                "La cliente apre il link da telefono e vede foto in galleria + PDF scaricabili / apribili.",
              ]} />
              <p>
                I PDF sono serviti con header inline ottimizzati per Safari iOS — la cliente può aprirli e
                salvarli nei File senza problemi.
              </p>
            </Section>

            <Section id="calendario-team" icon={Calendar} title="10. Calendario & Team">
              <h3>10.1 Calendario in Dashboard</h3>
              <p>
                Apri la <Pill>Dashboard</Pill> per vedere tutti gli eventi nella vista mensile.
              </p>
              <BulletList items={[
                "Filtri stato: Bozza, Inviati, Confermati, Rifiutati, Prove. Multi-selezione possibile.",
                "Filtri operatori: pill bar con dot colorato per ogni membro del team. Mostrano solo gli eventi dove quell'operatore è coinvolto (principale o assistente).",
                "Click su un giorno per vedere i dettagli.",
              ]} />

              <h3>10.2 Pagina Eventi</h3>
              <p>
                <Pill>Eventi</Pill> mostra l'agenda dei matrimoni in modalità lista. Gli eventi sono
                <strong> generati automaticamente</strong> dai clienti con contratto firmato e data
                matrimonio impostata. Non c'è creazione manuale: ogni voce nasce dal flusso reale del tuo lavoro.
              </p>

              <h3>10.3 Gestione team</h3>
              <p>
                Dalla pagina <Pill>Team</Pill> puoi:
              </p>
              <BulletList items={[
                "Aggiungere un nuovo membro (nome, ruolo, colore, email, telefono, specialità).",
                "Modificare un membro esistente (matita ✏️) — i dati si aggiornano ovunque, in tempo reale.",
                'Rimuovere un membro (cestino 🗑). Se ha clienti assegnati ti chiediamo conferma con cascade automatico delle assegnazioni.',
              ]} />
            </Section>

            <Section id="timeline" icon={ListChecks} title="11. Timeline evento">
              <p>
                La <strong>Timeline</strong> è la sequenza operativa del giorno del matrimonio: chi fa cosa
                e a che ora.
              </p>
              <BulletList items={[
                "Inserisci i blocchi attività (es. Trucco sposa → 06:30, Hair sposa → 07:30, Trucco mamma → 09:00).",
                "Specifica durata e operatore.",
                "Il sistema usa l'orario uscita sposa per controllare la coerenza dei tempi.",
              ]} />
              <p>
                Quando sei pronta, condividi la timeline con la cliente tramite link pubblico: lei la vede
                nel suo telefono e si sente coccolata e organizzata. Il giorno del matrimonio, tu hai
                l'agenda davanti agli occhi.
              </p>
            </Section>

            <Section id="mappa" icon={MapPin} title="12. Mappa">
              <p>
                Nella tab Mappa di ogni cliente trovi le location dell'evento (preparazione e cerimonia)
                visualizzate in modo chiaro. Ogni indirizzo è cliccabile e apre Google Maps per la
                navigazione: utile sia in fase di sopralluogo, sia il giorno del matrimonio per il team.
              </p>
              <p>
                Anche gli appuntamenti di prova hanno il loro link Google Maps, generato automaticamente
                dall'indirizzo che imposti.
              </p>
            </Section>

            <Section id="best-practice" icon={Lightbulb} title="13. Consigli operativi (best practice)">
              <h3>Da fare sempre</h3>
              <BulletList items={[
                "Configura tutto prima di lavorare con la prima cliente: contratto, dati fattura, team.",
                "Usa il link Lead Machine come punto di ingresso unico per le richieste — eviti di copiare-incollare info da WhatsApp.",
                "Inserisci subito orario uscita sposa, anche stimato. È il pilastro della timeline.",
                "Assegna un operatore principale per ogni servizio (makeup/hair) appena confermi: il calendario diventa subito leggibile.",
                "Carica le foto delle prove appena le fai, finché hai la cliente in studio.",
              ]} />

              <h3>Da evitare</h3>
              <BulletList items={[
                "Non creare la stessa cliente due volte (controlla la lista lead prima di aggiungere manuale).",
                "Non lasciare il contratto in stato non_inviato a ridosso del matrimonio: invia e fai firmare appena confermi la data.",
                "Non saltare la timeline: anche un evento singolo merita un piano.",
                "Non condividere il link Lead Machine in chat private senza tracciare: usa stories Instagram o link in bio.",
              ]} />

              <h3>Per velocizzare</h3>
              <BulletList items={[
                "Usa scorciatoie WhatsApp/Email integrate per condividere preventivo, contratto, foto e timeline.",
                "Configura un contratto personalizzato una volta sola: poi lo applichi in 1 click su qualsiasi cliente.",
                "Aggiungi i colori operatore: il calendario diventa parlante e identifichi i carichi di lavoro a colpo d'occhio.",
                "Sfrutta i filtri operatore in Dashboard quando devi pianificare la giornata di un singolo membro del team.",
              ]} />

              <h3>Strumenti pro</h3>
              <BulletList items={[
                "Forfettario: imposta il regime sul singolo preventivo, il PDF mostrerà la nota L.190/2014 e nasconderà l'IVA.",
                "Multilingua: cambia la lingua del cliente per generare PDF tradotti automaticamente.",
                "Foto + PDF prove: usa il link privato cliente come tocco premium dopo ogni prova.",
                "Cambio password periodico: in Impostazioni → Sicurezza, ogni 3-6 mesi.",
              ]} />
            </Section>

            {/* Closing note — il box "Hai bisogno di assistenza?" è
                ora in cima alla pagina, ben visibile. Manteniamo qui
                solo il rimando agli aggiornamenti per chi finisce di
                leggere e cerca novità di prodotto. */}
            <div className="pt-6 border-t border-border">
              <p className="text-xs text-neutral-500 flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
                Hai un dubbio non coperto da questa guida? Apri la sezione{" "}
                <Link to="/aggiornamenti" className="text-primary hover:underline">Aggiornamenti</Link>{" "}
                per le ultime novità o contatta il supporto.
              </p>
            </div>
          </article>
        </div>
      </main>

      <Footer variant="default" />

      {/* Inline typographic helpers — small content surface, kept local to this page */}
      <style>{`
        .prose-bridal h3 { font-family: inherit; font-size: 1.125rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: .5rem; color: #1f2937; }
        .prose-bridal p { color: #4b5563; line-height: 1.7; font-size: 0.95rem; }
        .prose-bridal strong { color: #1f2937; }
        .prose-bridal em { color: #6b7280; font-style: italic; }
      `}</style>
    </div>
  );
}

function Section({ id, icon: Icon, title, children }) {
  return (
    <section
      id={id}
      className="bg-white border border-border rounded-sm p-6 sm:p-10 scroll-mt-8"
      data-testid={`section-${id}`}
    >
      <header className="flex items-center gap-3 mb-5 pb-4 border-b border-border">
        {Icon && (
          <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
          </span>
        )}
        <h2 className="font-serif-heading text-2xl sm:text-3xl tracking-tight">{title}</h2>
      </header>
      <div className="space-y-3 text-sm leading-relaxed text-neutral-700">{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2 mt-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-neutral-700 leading-relaxed">
          <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" aria-hidden="true" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Callout({ tone = "primary", children }) {
  const cls = tone === "warn"
    ? "bg-amber-50 border-amber-200 text-amber-900"
    : "bg-primary/5 border-primary/20 text-neutral-800";
  return (
    <div className={`border rounded-sm px-4 py-3 text-sm leading-relaxed mt-3 ${cls}`}>
      {children}
    </div>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-block bg-secondary/70 border border-border rounded-sm px-2 py-0.5 text-xs font-medium text-neutral-800 align-baseline">
      {children}
    </span>
  );
}
